/**
 * Autopilot Service (AB#173)
 *
 * The recorded L1: what the platform thought, did, found, decided and would
 * have done for an incident, next to what people did. Read-only apart from the
 * adjudication, which is the benchmark's label. Nothing here acts on an
 * incident. L2/admin only — the API returns 403 (AUTOPILOT_L2_ONLY) otherwise.
 */
import { apiFetch, apiPost, buildQueryString, type ApiResponse } from './api';

export type Disposition = 'would-close' | 'would-hold-for-l2' | 'would-page';
export type Confidence = 'high' | 'medium' | 'low';
export type Lean = 'supports-benign' | 'supports-compromise' | 'neutral' | 'unobservable';
export type Agreement = 'agree' | 'conflict' | 'open';
export type AdjudicationVerdict = 'agree' | 'should-have-escalated' | 'should-have-closed' | 'wrong-reasoning-right-answer';

export interface AutopilotStep {
  round: number;
  presetId: string;
  presetName: string | null;
  question: string;
  params: Record<string, unknown> | null;
  status: string;
  rowCount: number | null;
  durationMs: number | null;
  finding: string | null;
  lean: Lean | null;
  rows: unknown[];
  notRun: string | null;
}

export interface AutopilotRecord {
  runId: string;
  tenantId: string;
  incidentId: string;
  correlationId: string | null;
  startedAt: string;
  completedAt: string;
  playbookId: string | null;
  playbookVersion: number | null;
  contentFingerprint: string | null;
  promptVersions: Record<string, string>;
  modelDeployment: string;
  pipelineVersion: string;
  intake: {
    incident: { title: string; severity: string | null; alertCount: number; defenderStatus: string | null; createdDateTime: string | null; entities: { users: string[]; devices: string[]; ips: string[]; files: string[] } };
    rules: Array<{ rules: string[]; source: string | null; meaning: string; benignWhen: string; compromiseWhen: string; scoreHint: string | null; askTheQueries: string[] }>;
    tenantContext: Record<string, unknown> | null;
    hypotheses: { benign: HypothesisCard; compromise: HypothesisCard };
  };
  investigation: {
    rounds: number;
    followUpsRun: number;
    stoppedBecause: string;
    budget: { limit: number; usedAtStart: number | null; readable: boolean; spent: boolean };
    /** `at` / `answeredAt` are stamped by records written after 2026-10-19; older ones carry neither. */
    roundQuestions: Array<{ round: number; question: string; presetIds: string[]; at?: string | null; answeredAt?: string | null }>;
    steps: AutopilotStep[];
  };
  analysis: { summary: string; timeline: Array<{ title: string; timestamp: string; description: string }>; tags: string[]; mitre: Array<{ tactic: string; technique: string }>; attackChain: string | null };
  verdict: {
    disposition: Disposition;
    modelDisposition: Disposition;
    dispositionReason: string;
    confidence: Confidence;
    riskScore: number;
    priority: string;
    scoringRuleApplied: string;
    evidenceFor: Array<{ presetId: string | null; reason: string }>;
    evidenceAgainst: Array<{ presetId: string | null; reason: string }>;
    unknowns: Array<{ what: string; why: string }>;
    reasoning: { ruleMeaning: string; evidence: string; decision: string };
    /** Expected-pattern rows the verdict relied on (records written after 2026-10-19; older ones carry none). */
    expectedPatternsApplied?: AppliedExpectedPattern[];
  };
  next: {
    closure: { classification: string; determination: string; comment: string } | null;
    escalationNote: string | null;
    customerMessage: { audience: 'none' | 'inform' | 'request-approval'; text: string | null };
    actions: Array<{ actionKey: string | null; action: string; authorisation: 'pre-authorised' | 'requires-approval' | 'never'; condition: string | null; target: string | null; wouldHaveProposed: boolean }>;
  };
  usage: { promptTokens: number; completionTokens: number; modelCalls: number; durationMs: number };
}

/** A dbo.TenantExpectedPatterns row triage matched to the incident (alexein/feature-adds/tenant_expected_patterns.md). */
export interface ExpectedPatternMatch {
  patternId: string;
  scope: string;
  entityType: string;
  entityValue: string;
  condition: string | null;
  reading: 'expected-benign' | 'watch' | 'never-benign';
  rationale: string;
  confirmedBy: string | null;
  matchedOn: { entityType: string; value: string };
}

export interface AppliedExpectedPattern {
  patternId: string;
  howItApplied: string;
  /** Null when the model cited an id it was not offered. */
  row: ExpectedPatternMatch | null;
}

export interface HypothesisCard {
  reading: 'benign' | 'compromise';
  statements: Array<{ rule: string; text: string }>;
  confirmingQueries: string[];
}

export interface Adjudication {
  by: string | null;
  at: string | null;
  verdict: AdjudicationVerdict;
  note: string | null;
}

export interface HumanOutcome {
  platformStatus: string | null;
  classification: string | null;
  determination: string | null;
  assignedToName: string | null;
  arrivedAt: string | null;
  firstHumanActionAt: string | null;
  closedAt: string | null;
  minutesToFirstAction: number | null;
  minutesToClose: number | null;
  transitions: Array<{ from: string | null; to: string; byUserId: string | null; byName: string | null; at: string; reason: string | null; actor?: 'autopilot' }>;
  /** The incident's analyst notes in full, newest first. */
  notes?: Array<{ noteId: string; authorName: string | null; noteType: string; content: string; createdAt: string }>;
}

export type ExecutionDecision = 'would-execute' | 'executed' | 'skipped' | 'not-eligible' | 'paused' | 'disabled';
export interface AutopilotExecution {
  decision: ExecutionDecision;
  reason: string | null;
  executedAt: string | null;
  policyAt?: string | null;
}

export interface IncidentAutopilot {
  run: {
    runId: string; startedAt: string; completedAt: string; createdAt: string;
    playbookId: string | null; playbookVersion: number | null; contentFingerprint: string | null;
    promptVersions: Record<string, string> | null; modelDeployment: string; pipelineVersion: string;
    disposition: Disposition; modelDisposition: Disposition; confidence: Confidence; riskScore: number | null; scoringRuleApplied: string | null;
    rounds: number; queriesRun: number; unknownCount: number; wouldHaveProposedCount: number;
    adjudication: Adjudication | null;
    /** What the executor did with the verdict (autopilot_auto_close.md §6); null until it has looked at the record. */
    execution?: AutopilotExecution | null;
    record: AutopilotRecord | null;
  };
  incident: { title: string | null; severity: string | null } | null;
  human: HumanOutcome;
  agreement: Agreement;
  /** Whether the signed-in user may record an adjudication (the named adjudicators / closure approvers). Absent on older responses = allowed. */
  canAdjudicate?: boolean;
}

export interface AutopilotListRow {
  runId: string; tenantId: string; tenantDisplayName: string; incidentId: string; title: string | null; rule: string; severity: string | null; createdAt: string;
  disposition: Disposition; modelDisposition: Disposition; confidence: Confidence; riskScore: number | null; scoringRuleApplied: string | null;
  rounds: number; queriesRun: number; wouldHaveProposedCount: number;
  adjudication: { verdict: AdjudicationVerdict; by: string | null; at: string | null } | null;
  execution?: AutopilotExecution | null;
  human: { platformStatus: string | null; classification: string | null; determination: string | null; assignedToName: string | null };
  agreement: Agreement;
}

export interface AutopilotListFilters {
  tenantId?: string; rule?: string; disposition?: string; agreement?: string; adjudicated?: 'true' | 'false';
  /** executed | would-execute | not-eligible | paused | disabled | skipped | pending | decided */
  execution?: string;
  dateFrom?: string; dateTo?: string; limit?: number; offset?: number;
}

export interface AutopilotClassSummary {
  tenantId: string; tenantDisplayName: string; rule: string; severity: string; runs: number;
  dispositions: Record<string, number>; agreement: Record<string, number>; agreementRate: number | null;
  adjudicated: number; adjudications: Record<string, number>; shouldHaveEscalated: number;
}

export async function getIncidentAutopilot(tenantId: string, incidentId: string): Promise<ApiResponse<IncidentAutopilot>> {
  return apiFetch<IncidentAutopilot>(`/incidents/${tenantId}/${incidentId}/autopilot`);
}

export async function adjudicateAutopilot(tenantId: string, incidentId: string, verdict: AdjudicationVerdict, note: string): Promise<ApiResponse<{ runId: string; adjudication: Adjudication }>> {
  return apiPost(`/incidents/${tenantId}/${incidentId}/autopilot/adjudicate`, { verdict, note });
}

export async function getAutopilotRuns(filters: AutopilotListFilters): Promise<ApiResponse<{ runs: AutopilotListRow[]; pagination: { total: number; limit: number; offset: number } }>> {
  return apiFetch(`/autopilot${buildQueryString({ ...filters })}`);
}

export async function getAutopilotSample(): Promise<ApiResponse<{ runs: AutopilotListRow[] }>> {
  return apiFetch(`/autopilot?sample=1`);
}

export async function getAutopilotSummary(filters: { tenantId?: string; dateFrom?: string; dateTo?: string }): Promise<ApiResponse<{ summary: AutopilotClassSummary[]; totals: { runs: number; adjudicated: number; shouldHaveEscalated: number; dispositions: Record<string, number>; agreement: Record<string, number> } }>> {
  return apiFetch(`/autopilot/summary${buildQueryString({ ...filters })}`);
}

/** Display vocabulary shared by the banner, the tab and the list. */
export const DISPOSITION_LABEL: Record<Disposition, string> = {
  'would-close': 'Would have closed',
  'would-hold-for-l2': 'Would have held for L2',
  'would-page': 'Would have paged',
};
export const ADJUDICATION_LABEL: Record<AdjudicationVerdict, string> = {
  agree: 'Agree',
  'should-have-escalated': 'Should have escalated',
  'should-have-closed': 'Should have closed',
  'wrong-reasoning-right-answer': 'Right answer, wrong reasoning',
};
export const DISPOSITION_STYLE: Record<Disposition, string> = {
  'would-close': 'bg-accent-green/15 text-accent-green border-accent-green/40',
  'would-hold-for-l2': 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/40',
  'would-page': 'bg-accent-red/15 text-accent-red border-accent-red/40',
};
export const AGREEMENT_STYLE: Record<Agreement, string> = {
  agree: 'bg-accent-green/15 text-accent-green border-accent-green/40',
  conflict: 'bg-accent-red/15 text-accent-red border-accent-red/40',
  open: 'bg-bg-tertiary text-text-secondary border-border-default',
};
export const AGREEMENT_LABEL: Record<Agreement, string> = { agree: 'Agrees with the analyst', conflict: 'Conflicts with the analyst', open: 'Incident still open' };
export const EXECUTION_LABEL: Record<ExecutionDecision, string> = {
  'would-execute': 'Would execute',
  executed: 'Closed by Autopilot',
  skipped: 'Skipped',
  'not-eligible': 'Not eligible',
  paused: 'Paused',
  disabled: 'Policy disabled',
};
export const EXECUTION_STYLE: Record<ExecutionDecision, string> = {
  'would-execute': 'bg-accent-blue/15 text-accent-blue border-accent-blue/40',
  executed: 'bg-accent-green/15 text-accent-green border-accent-green/40',
  skipped: 'bg-bg-tertiary text-text-secondary border-border-default',
  'not-eligible': 'bg-bg-tertiary text-text-secondary border-border-default',
  paused: 'bg-accent-red/15 text-accent-red border-accent-red/40',
  disabled: 'bg-bg-tertiary text-text-secondary border-border-default',
};
